import { loadVendorOptions } from '../vendorOptions';
import { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';

interface ProductOption { id: string; name: string; sku: string }
interface VendorOption { id: string; name: string }
interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (po: { poId: string; number: string; total: number }) => void;
}

export function QuickPoModal({ open, onClose, onCreated }: Props) {
  // Unmount the form on close so a new quick PO always starts with empty fields.
  return open ? <QuickPoForm onClose={onClose} onCreated={onCreated} /> : null;
}

function QuickPoForm({ onClose, onCreated }: Omit<Props, 'open'>) {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [product, setProduct] = useState<ProductOption | null>(null);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [qty, setQty] = useState('');
  const [rate, setRate] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    loadVendorOptions(controller.signal).then(rows => { if (active) setVendors(rows); })
      .catch(() => { if (active) setError('Failed to load suppliers. Close and reopen the quick PO to try again.'); });
    return () => { active = false; controller.abort(); };
  }, []);

  useEffect(() => {
    setOptions([]);
    if (product || search.trim().length < 2) return;
    let active = true;
    const timer = setTimeout(() => {
      api.get('/products', { params: { search, limit: 10 } }).then(r => { if (active) setOptions(r.data?.data ?? []); })
        .catch(() => { if (active) setError('Failed to find items. Please try searching again.'); });
    }, 200);
    return () => { active = false; clearTimeout(timer); };
  }, [search, product]);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const res = await api.post('/purchase-orders/quick', { productId: product?.id, qty: Number(qty), rate: Number(rate), vendorId });
      onCreated(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to place the quick PO');
    } finally {
      setSaving(false);
    }
  };
  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  // Shell classes follow PurchaseRequestsPage's New Purchase Request modal.
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { if (!saving) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="qpo-title"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="qpo-title" className="text-lg font-semibold text-gray-900 dark:text-white">⚡ Quick PO</h2>
          <button disabled={saving} onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="qpo-item" className={labelClass}>Item</label>
            <input id="qpo-item" disabled={saving} value={product ? product.name : search}
              onChange={e => { setProduct(null); setSearch(e.target.value); }} className={inputClass} />
            {!product && options.length > 0 && (
              <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-700">
                {options.map(o => <button key={o.id} type="button" onClick={() => setProduct(o)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600">
                  {o.name} <span className="text-xs text-gray-400">{o.sku}</span>
                </button>)}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label htmlFor="qpo-qty" className={labelClass}>Qty</label>
              <input id="qpo-qty" type="number" min="0" step="any" disabled={saving} value={qty} onChange={e => setQty(e.target.value)} className={inputClass} /></div>
            <div><label htmlFor="qpo-rate" className={labelClass}>Rate</label>
              <input id="qpo-rate" type="number" min="0" step="any" disabled={saving} value={rate} onChange={e => setRate(e.target.value)} className={inputClass} /></div>
          </div>
          <div><label htmlFor="qpo-vendor" className={labelClass}>Supplier</label>
            <select id="qpo-vendor" disabled={saving} value={vendorId} onChange={e => setVendorId(e.target.value)} className={inputClass}>
              <option value="">Select supplier…</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <p className="text-xs text-gray-500">Up to ₹20 L before GST only. Flagged for post-facto approval.</p>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button disabled={saving} onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</button>
          <button onClick={submit} disabled={saving || !product || !qty || !rate || !vendorId}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}Place quick PO
          </button>
        </div>
      </div>
    </div>
  );
}
